import { useRef } from "react";
import { motion, useScroll } from "framer-motion";
import { Check } from "lucide-react";
import { EASE } from "@/lib/lenis";
import { MOCKUP_LINK } from "@/content/site";
import { GlowField } from "@/components/site/GlowField";
import { CTAButton } from "@/components/site/CTAButton";
import { MaskedLines } from "@/components/motion/Reveal";
import { HeroMockup } from "@/components/home/HeroMockup";

const fade = (delay) => ({
  initial: { opacity: 0, y: 18 },
  animate: { opacity: 1, y: 0 },
  transition: { duration: 0.9, ease: EASE, delay },
});

export const Hero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });

  return (
    <section ref={ref} className="relative isolate overflow-hidden pt-32 sm:pt-40" data-testid="home-hero">
      <GlowField />
      <div className="hero-lines pointer-events-none absolute inset-0 -z-10" aria-hidden="true" />
      <div className="container-x">
        <motion.p
          {...fade(0)}
          className="inline-flex items-center gap-2 rounded-full border border-white/10 bg-white/[0.03] px-3 py-1.5 text-xs text-white/85 backdrop-blur"
          data-testid="hero-eyebrow"
        >
          <span className="relative flex h-1.5 w-1.5">
            <span className="absolute inset-0 animate-ping rounded-full bg-[#2DD4D4] opacity-60 motion-reduce:hidden" />
            <span className="relative h-1.5 w-1.5 rounded-full bg-[#2DD4D4]" />
          </span>
          Web design & automation studio · Wayne, NJ
        </motion.p>
        <MaskedLines
          as="h1"
          lines={["Fast, modern websites", "for North Jersey", "small businesses."]}
          delay={0.12}
          testId="hero-headline"
          className="mt-7 text-[2.6rem] font-semibold leading-[1.0] tracking-[-0.045em] sm:text-6xl lg:text-7xl xl:text-[5.6rem]"
          lineClassName="text-sheen"
        />
        <div className="mt-10 grid gap-8 lg:grid-cols-[1fr_auto] lg:items-end">
          <motion.div {...fade(0.55)}>
            <p className="text-xl font-medium tracking-tight text-white/90 sm:text-2xl" data-testid="hero-supporting-line">
              Live in <span className="text-[#2DD4D4]">7–10 days.</span>
            </p>
            <div className="mt-7 flex flex-wrap gap-3">
              <CTAButton to={MOCKUP_LINK} size="lg" testId="hero-primary-cta">
                Get a free mockup
              </CTAButton>
              <CTAButton to="/work" variant="secondary" size="lg" arrow={false} testId="hero-secondary-cta">
                See our work
              </CTAButton>
            </div>
          </motion.div>
          <motion.ul
            {...fade(0.7)}
            aria-label="Local. Fast. Fixed prices."
            data-testid="hero-trust-line"
            className="flex flex-wrap gap-x-6 gap-y-2 font-mono text-xs uppercase tracking-[0.18em] text-[#A1A1AA] lg:flex-col lg:items-end"
          >
            {["Local.", "Fast.", "Fixed prices."].map((item) => (
              <li key={item} className="flex items-center gap-2">
                <Check className="h-3.5 w-3.5 text-[#2F6BFF]" aria-hidden="true" />
                {item}
              </li>
            ))}
          </motion.ul>
        </div>
      </div>
      <HeroMockup progress={scrollYProgress} />
    </section>
  );
};
